import {registerMaterialGraphNode} from "#material/register"
import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {ScannedTransmissionSockets} from "#material/nodes/shader/scanned-transmission/definition"
import {scannedTransmissionDefinition} from "#material/nodes/shader/scanned-transmission/definition"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {materialConnection} from "#material/nodes/base/connection/shorthand"
import {BsdfTranslucent} from "#material/nodes/shader/bsdf-translucent/bsdf-translucent"
import {AddShader} from "#material/nodes/shader/add-shader/add-shader"
import type {CyclesReturns} from "#material/sockets/cycles"
import {ThreeColormassMaterial} from "#material/three/three-colormass-material"
import type {ThreeReturns} from "#material/sockets/three"
import {vectorMul} from "#material/nodes/converter/vector-math/shorthand"
import {adjustWorldNormalStrength, rescaleColorValue} from "#material/nodes/shader/scanned-transmission/helpers"
import {BsdfDiffuse} from "#material/nodes/shader/bsdf-diffuse/bsdf-diffuse"
import {mul} from "#material/nodes/converter/scalar-math/shorthands"
import {Three as THREE} from "#material/three/exports"

@registerMaterialGraphNode
export class ScannedTransmission extends MaterialNode<ScannedTransmissionSockets> {
    public get definition() {
        return scannedTransmissionDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<ScannedTransmissionSockets>> => {
        // just bypass this in ThreeJS mode for now
        const inputBsdf = await evaluator.get(this.parameters.inputs.bsdf)
        if (!inputBsdf) return {bsdf: new ThreeColormassMaterial()}
        if (!(inputBsdf instanceof THREE.Material)) throw new Error("ScannedTransmission: bsdf input must be a THREE.Material in ThreeJS mode")
        return {bsdf: inputBsdf}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<ScannedTransmissionSockets>> => {
        const {bsdf} = this.parameters.inputs

        const normalStrength = this.parameters.inputs.normalStrength ?? this.parameters.settings.normalStrength
        const minTransmission = this.parameters.inputs.minTransmission ?? this.parameters.settings.minTransmission
        const alpha = this.parameters.inputs.alpha ?? this.parameters.settings.alpha
        const amount = this.parameters.inputs.amount ?? this.parameters.settings.amount

        // an unconnected Transmission bypasses the node, and an unconnected Normal has to reach the geometric
        // normal, so neither takes its stored default the way the scalars above do
        const transmission = this.parameters.inputs.transmission
        if (!transmission) {
            return {
                bsdf: await evaluator.get(
                    bsdf ??
                        materialConnection(
                            new BsdfDiffuse({
                                parameters: {
                                    inputs: {},
                                    settings: {color: {r: 0.0, g: 0.0, b: 0.0}},
                                },
                            }),
                            "bsdf",
                        ),
                ),
            }
        }

        const scale = alpha && amount ? mul(alpha, amount) : (amount ?? alpha ?? 1.0)

        const translucentBsdf = materialConnection(
            new BsdfTranslucent({
                parameters: {
                    inputs: {
                        color: vectorMul(rescaleColorValue(transmission, minTransmission ?? 0, 1), scale),
                        normal: this.parameters.inputs.normal ? adjustWorldNormalStrength(this.parameters.inputs.normal, normalStrength ?? 1) : undefined,
                    },
                    settings: {},
                },
            }),
            "bsdf",
        )

        const addedShader = materialConnection(
            new AddShader({
                parameters: {
                    inputs: {
                        shader: bsdf,
                        shader_001: translucentBsdf,
                    },
                    settings: {},
                },
            }),
            "shader",
        )

        return {bsdf: await evaluator.get(addedShader)}
    }
}
